import { useCallback, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import {
	useContributionsPageState,
	useContributionsPageData,
} from '#src/app/pages/accountSettings/contributions/main/pageContext';

import { useCancelSubscription } from '../../../../hooks/useCancelSubscription';

import { useClickLowerByHalf } from './hooks/useClickLowerByHalf';

type Result = ModelHookResult<
	{
		title: string;
		text: string;
		cancelLabel: string;
		lowerLabel: string;
		lowerByHalfLoading: boolean;
		cancelLoading: boolean;
	},
	{
		clickLowerByHalf: () => Promise<void>;
		clickCancelSubscription: (onBeforeCaptcha?: () => void) => Promise<void>;
		resetState: () => void;
		clickClose: () => void;
	}
>;

export function useCancelSubscriptionUpsellModal(): Result {
	const { data } = useContributionsPageData();
	const [{ subscription: subs }] = useContributionsPageState();
	const { translate, localizeCurrency } = useI18n();
	const cancelSubscription = useCancelSubscription();
	const [cancelLoading, setCancelLoading] = useState(false);
	const [lowerByHalfLoading, setLowerByHalfLoading] = useState(false);
	const track = useTracking();
	const newAmountValue = Math.max(Math.ceil(subs.amount / 2), data.subscription.config.minimumAmount);
	const newAmount = localizeCurrency(newAmountValue, subs.currencyCode, { currencyDisplay: 'narrowSymbol' });
	const lowerByHalf = useClickLowerByHalf(newAmountValue);

	const clickLowerByHalf = useCallback(async () => {
		setLowerByHalfLoading(true);
		await lowerByHalf();
	}, [lowerByHalf]);

	const clickCancelSubscription = useCallback(
		async (onBeforeCaptcha?: () => void) => {
			setCancelLoading(true);
			await cancelSubscription({ onBeforeCaptcha });
		},
		[cancelSubscription],
	);

	const resetState = useCallback(() => {
		setCancelLoading(false);
		setLowerByHalfLoading(false);
	}, []);

	const clickClose = useCallback(() => {
		void track('subs_cancel_modal_stay_click');
		resetState();
	}, [track, resetState]);

	const lowerLabel =
		subs.interval === 'YEAR'
			? translate('corgi.membership.manage.lower_amount.yearly_lower_amount_button', { amount: newAmount })
			: translate('corgi.membership.manage.lower_amount.monthly_lower_amount_button', { amount: newAmount });

	return {
		data: {
			title: translate('corgi.membership.manage.lower_amount.heading'),
			text: translate('corgi.membership.manage.lower_amount.subtext'),
			cancelLabel: translate('corgi.membership.manage.lower_amount.cancel_button'),
			lowerLabel,
			cancelLoading,
			lowerByHalfLoading,
		},
		actions: {
			clickLowerByHalf,
			clickCancelSubscription,
			resetState,
			clickClose,
		},
	};
}
